import { Chip, LinearProgress, Paper, Typography, Container, Link, Box } from "@mui/material";
import { useEffect, useState } from "react";
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import { useParams } from "react-router-dom";
import ArticleTitle from "./ArticleTitle";
import RetractionReasonList from "./RetractionReasonList";
import { Helmet } from "react-helmet";
import { RetractedWork } from "./CommonInterfaces";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";

interface DoiPanelProps {
  partialDoiProp?: string,
  updateFunction?: (e: string) => void
}

function DoiPanel({ partialDoiProp, updateFunction }: DoiPanelProps) {

  const [latestRetraction, setLatestRetraction] = useState<RetractedWork>()
  const [searchInProgress, setSearchInProgress] = useState(false)
  let showHeader = partialDoiProp === undefined


  let params = useParams();
  let partialDoiFromParams = params["*"]

  let partialDoi = partialDoiProp !== undefined ? partialDoiProp : partialDoiFromParams


  useEffect(() => {

    async function fetchData() {
      let authToken = await auth.currentUser?.getIdToken(true)

      if (partialDoi !== undefined) {
        setSearchInProgress(true);
        fetch(argosApiEntryPoint + '/retraction?search=doi:' + partialDoi, {
          method: 'GET',
          headers: (authToken) ? {
            'content-type': 'application/json;charset=UTF-8',
            'Authorization': 'Bearer ' + authToken
          } : {
            'content-type': 'application/json;charset=UTF-8'
          }
        })
          .then((response) => {
            if(response.status === 200){
              response.json().then( data => {
                setSearchInProgress(false);
                setLatestRetraction(data);
                if(updateFunction != undefined){
                  updateFunction(data.retractionNature)
                }
              })
            } else if (response.status === 404) {
              setSearchInProgress(false);
              if(updateFunction != undefined){
                updateFunction('not-retracted')
              }
            }
          }
        )
      }
    }
    fetchData()

    // TODO : Error handling setError(true)
  }, [partialDoi]);

  return (
    <>
      <div id="doi-analysis" />

      {searchInProgress &&
        <LinearProgress />}

      {latestRetraction &&
        <Container maxWidth="xl" sx={{
          ...(!showHeader && {
            p: 0, m: 0, mr: 0
          })
        }}>
          {showHeader &&
            <>
              <Helmet>
                <title>{latestRetraction.title}</title>
                <meta name="description" content={"Retraction data and analysis by Argos: " + latestRetraction.title + ", authorship: " + latestRetraction.authors.join(", ")} />
                <meta name="keywords" content={latestRetraction.originalPaperDoi + "," + latestRetraction.title.split(" ").join(",") + "," + latestRetraction.retractionDoi + "," + latestRetraction.authors} />
              </Helmet>
              <Typography variant="h2">
                {latestRetraction.retractionNature}</Typography>
              <Paper sx={{ p: 3 }}>
                <Typography gutterBottom variant="h1" sx={{ fontSize: '2em' }}>
                  <ArticleTitle title={latestRetraction.title} />
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{
                    mr: 1,
                    mt: 1
                  }}>Authors:</Typography>
                  {latestRetraction.authors.map(author =>
                    <Chip
                      label={author}
                      variant="outlined"
                      component="h3"
                      color="info"
                      size="small"
                      sx={{
                        mr: 1,
                        mt: 1,
                        fontWeight: "regular",
                        backgroundColor: '#FFFFFF'
                      }}
                    />
                  )}
                </Box>
                <Chip component="h3" icon={<ImportContactsIcon />} label={"Journal: " + latestRetraction.journal + " / Publisher: " + latestRetraction.publisher} variant="outlined" sx={{
                  mr: 1,
                  mt: 1,
                  backgroundColor: '#FFFFFF',
                  fontWeight: "regular"
                }} />
                <Chip component="h3" icon={<CalendarMonthIcon />} label={"Publication date:" + latestRetraction.originalPaperDate} variant="outlined" sx={{
                  mr: 1,
                  mt: 1,
                  fontWeight: "regular",
                  backgroundColor: '#FFFFFF'
                }} />
                <Link href={latestRetraction.originalPaperDoi} target="_blank">
                  <Chip component="h3" icon={<FingerprintIcon />} label={"Original DOI:" + latestRetraction.originalPaperDoi} variant="outlined" sx={{
                    mr: 1,
                    mt: 1,
                    fontWeight: "regular",
                    backgroundColor: '#FFFFFF'
                  }} /></Link>
              </Paper>
            </>
          }
          <Paper elevation={1} sx={{
            ...(showHeader && {
              backgroundColor: '#f2f4f7', p: 5, mt: 2
            }),
            ...(!showHeader && {
              backgroundColor: 'rgb(242, 244, 247)', p: 2, mb: 2
            })
          }}>
            {latestRetraction.retractionDoi && latestRetraction.retractionDoi !== 'Unknown' &&
              <Typography variant="body2" component="div">
                Retraction DOI:
                <Link href={latestRetraction.retractionDoi} target="_blank">
                  <Chip icon={<FingerprintIcon />} label={latestRetraction.retractionDoi} component="h3" variant="outlined" sx={{
                    mr: 1,
                    mt: 1,
                    fontWeight: "regular",
                    backgroundColor: '#FFFFFF',

                  }} /></Link>
              </Typography>}
            <Typography variant="body2" component="div">
              Retraction source:
              <Chip label={latestRetraction.source} variant="outlined" sx={{
                mr: 1,
                mt: 1,
                backgroundColor: '#FFFFFF',
              }} />
            </Typography>
            {latestRetraction.retractionDate && latestRetraction.retractionDate !== 'null' &&
              <Typography variant="body2" component="div">
                Retraction date:
                <Chip icon={<CalendarMonthIcon />} label={latestRetraction.retractionDate} variant="outlined" sx={{
                  mr: 1,
                  mt: 1,
                  backgroundColor: '#FFFFFF',
                }} />
              </Typography>}
            {latestRetraction.reasons && latestRetraction.reasons.length > 0 &&
              <Typography gutterBottom variant="body2" component="div">
                Retraction Reason(s):
                <RetractionReasonList reasons={latestRetraction.reasons} />
              </Typography>
            }
            {latestRetraction.notes &&
              <Typography gutterBottom variant="body1" component="div">
                Notes(s): {latestRetraction.notes}
              </Typography>}
          </Paper>  

        </Container>
      }
    </>
  )
}

export default DoiPanel;